type CareerApiItem = {
  data?: {
    career?: {
      key?: string;
      title?: string;
      visible?: boolean;
      cta?: Array<{
        heroSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          headline?: string;
          primaryButton?: {
            key?: string;
            label?: string;
            visible?: boolean;
          };
        };
        positionSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          content?: Array<{
            key?: string;
            listSection?: {
              key?: string;
              visible?: boolean;
              list?: {
                visible?: boolean;
                items?: Array<{
                  id?: string;
                  code?: string;
                  title?: string;
                  location?: string;
                }>;
              };
            };
            headerSection?: {
              key?: string;
              label?: string;
              title?: string;
              visible?: boolean;
              actionButton?: {
                label?: string;
                visible?: boolean;
              };
            };
          }>;
        };
        submissionStepSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          content?: Array<{
            key?: string;
            headerSection?: {
              key?: string;
              label?: string;
              title?: string;
              visible?: boolean;
              description?: string;
              list?: {
                visible?: boolean;
                items?: Array<{
                  id?: string;
                  step?: string;
                  title?: string;
                  description?: string;
                  button?: {
                    title?: string;
                  };
                }>;
              };
            };
          }>;
        };
      }>;
    };
  };
};

export type NormalizedCareer = {
  page: "career";
  title: string;
  visible: boolean;

  hero: {
    headline: string;
    visible: boolean;
    primaryButton: {
      label: string;
      visible: boolean;
    };
  };

  positions: {
    visible: boolean;
    header: {
      label: string;
      title: string;
      visible: boolean;
      actionButton: {
        label: string;
        visible: boolean;
      };
    };
    list: {
      visible: boolean;
      items: Array<{
        id: string;
        code: string;
        title: string;
        location: string;
      }>;
    };
  };

  submissionSteps: {
    visible: boolean;
    header: {
      label: string;
      title: string;
      description: string;
      visible: boolean;
    };
    list: {
      visible: boolean;
      items: Array<{
        id: string;
        step: string;
        title: string;
        description: string;
        buttonTitle: string;
      }>;
    };
  };

  meta: {
    key: string;
  };
};

export function normalizeCareer(item: CareerApiItem): NormalizedCareer | null {
  const career = item?.data?.career;
  const cta = career?.cta?.[0];
  const hero = cta?.heroSection;
  const position = cta?.positionSection;
  const positionContent = position?.content?.[0];
  const submission = cta?.submissionStepSection;
  const submissionContent = submission?.content?.[0];

  if (!career || !cta) return null;

  return {
    page: "career",
    title: career.title ?? "",
    visible: Boolean(career.visible),

    hero: {
      headline: hero?.headline ?? "",
      visible: Boolean(hero?.visible),
      primaryButton: {
        label: hero?.primaryButton?.label ?? "",
        visible: Boolean(hero?.primaryButton?.visible),
      },
    },

    positions: {
      visible: Boolean(position?.visible),
      header: {
        label: positionContent?.headerSection?.label ?? "",
        title: positionContent?.headerSection?.title ?? "",
        visible: Boolean(positionContent?.headerSection?.visible),
        actionButton: {
          label: positionContent?.headerSection?.actionButton?.label ?? "",
          visible: Boolean(positionContent?.headerSection?.actionButton?.visible),
        },
      },
      list: {
        visible: Boolean(
          positionContent?.listSection?.list?.visible ?? positionContent?.listSection?.visible
        ),
        items: (positionContent?.listSection?.list?.items ?? []).map((job) => ({
          id: job.id ?? "",
          code: job.code ?? "",
          title: job.title ?? "",
          location: job.location ?? "",
        })),
      },
    },

    submissionSteps: {
      visible: Boolean(submission?.visible),
      header: {
        label: submissionContent?.headerSection?.label ?? "",
        title: submissionContent?.headerSection?.title ?? "",
        description: submissionContent?.headerSection?.description ?? "",
        visible: Boolean(submissionContent?.headerSection?.visible),
      },
      list: {
        visible: Boolean(submissionContent?.headerSection?.list?.visible),
        items: (submissionContent?.headerSection?.list?.items ?? []).map((step) => ({
          id: step.id ?? "",
          step: step.step ?? "",
          title: step.title ?? "",
          description: step.description ?? "",
          buttonTitle: step.button?.title ?? "",
        })),
      },
    },

    meta: {
      key: career.key ?? "",
    },
  };
}